import {
  Accordion,
  AccordionButton,
  AccordionIcon,
  AccordionItem,
  AccordionPanel,
  Box,
  // Button,
  Card,
  Flex,
  HStack,
  Heading,
  Link,
  Popover,
  PopoverArrow,
  PopoverBody,
  PopoverContent,
  PopoverTrigger,
  Text,
  VStack
} from "@chakra-ui/react"
import { useTranslate } from "@pathwright/ui/src/components/lng/withTranslate"
import Pathicon from "@pathwright/ui/src/components/pathicon/Pathicon"
import When from "@pathwright/ui/src/components/when/When"
import BlocksPublish from "@pathwright/web/src/modules/blocks/StandaloneBlocksPublish"
import { getSyncUrl } from "@pathwright/web/src/modules/path/sync/utils"
import { getStepUrl } from "@pathwright/web/src/modules/utils/urls"
import { ReactElement, useRef, useState } from "react"
import SuggestedAction from "../../path/sync/SuggestedAction"
import { useCohortSyncPlanQuery } from "../../path/sync/SyncPlanContext"
import { FromCohort, PathItem } from "../../path/sync/types"
import DotBackground from "./DotBackground"
import ResourceBackground from "./ResourceBackground"
import { PublishIcon } from "./StatusIconButton"
import StepPublishProvider, { usePublish } from "./StepPublishProvider"
import StepPublishTrigger from "./StepPublishTrigger"
import { stepPublishStatuses } from "./utils"

type PublishSectionContentProps = {
  heading: string
  label: ReactElement | string | null
  callout: ReactElement | string | null
  buttonUrl?: string
  buttonLabel?: ReactElement | string
  renderButton?: () => JSX.Element
}

const PublishSectionContent = ({
  heading,
  label = null,
  callout,
  buttonUrl,
  buttonLabel,
  renderButton
}: PublishSectionContentProps) => {
  return (
    <VStack align="center" p={5} gap="16px">
      <VStack gap={1}>
        <PublishIcon iconOnly />
        <Heading as="h3" size="md" textAlign="center" my={0}>
          {heading}
        </Heading>
      </VStack>
      {label}
      {callout}
      {renderButton ? (
        renderButton()
      ) : buttonLabel && buttonUrl ? (
        <Box mt={1} mb={2} maxW="100%">
          <Box
            _hover={{
              ".chakra-button": {
                borderColor: "rgba(0, 0, 0, 0.2)",
                color: "rgba(0, 0, 0, 0.4)",
                backgroundColor: "rgba(0, 0, 0, 0.1)"
              },
              svg: {
                fill: "rgba(0, 0, 0, 0.3)"
              }
            }}
            sx={{
              ".chakra-button": {
                borderColor: "rgba(0, 0, 0, 0.3)",
                color: "rgba(0, 0, 0, 0.4)",
                fontWeight: "400"
              },
              svg: {
                fill: "rgba(0, 0, 0, 0.3)"
              }
            }}
          >
            <SuggestedAction url={buttonUrl} label={buttonLabel} />
          </Box>
        </Box>
      ) : null}
    </VStack>
  )
}

const PublishSection = () => {
  const { cohort, publishStatus, item, resource, onClose, onOpenStep } =
    usePublish()

  const { tc, Trans } = useTranslate()

  if (!item || !cohort) return null

  const stepURL = getStepUrl(resource.slug, cohort.id, item.id)

  let label: string | ReactElement | null = null
  let callout = null
  let heading = ""
  let buttonLabel = undefined
  let buttonUrl = undefined
  let renderButton = undefined

  if (publishStatus === stepPublishStatuses.contentEmpty) {
    heading = tc("Draft")
    label = (
      <Text textAlign="center">
        <Trans i18nKey="this-step-is-missing-content">
          <Link color="brand.500" href={stepURL}>
            This Step
          </Link>{" "}
          is missing content.
        </Trans>
      </Text>
    )
    renderButton = () => (
      <Box mt={1} mb={2} maxW="100%">
        <Box
          _hover={{
            ".chakra-button": {
              borderColor: "rgba(0, 0, 0, 0.2)",
              color: "rgba(0, 0, 0, 0.4)",
              backgroundColor: "rgba(0, 0, 0, 0.1)"
            },
            svg: {
              fill: "rgba(0, 0, 0, 0.3)"
            }
          }}
          sx={{
            ".chakra-button": {
              borderColor: "rgba(0, 0, 0, 0.3)",
              color: "rgba(0, 0, 0, 0.4)",
              fontWeight: "400"
            },
            svg: {
              fill: "rgba(0, 0, 0, 0.3)"
            }
          }}
        >
          <SuggestedAction url={stepURL} label={tc("Add Blocks")} />
        </Box>
      </Box>
    )
  } else if (
    publishStatus === stepPublishStatuses.contentDraft ||
    publishStatus === stepPublishStatuses.contentUnpublished
  ) {
    heading = tc("Draft Changes")

    const isMultiStepChange =
      item.id !== item.source_id && (resource.cohorts?.total || 0) > 1

    label = (
      <HStack justify="center" w="full">
        <Text textAlign="center">
          <Trans i18nKey="this-step-has-draft-block-changes">
            <Link
              color="brand.500"
              href={getStepUrl(resource.slug, cohort.id, item.id)}
              onClick={(event) => {
                if (onOpenStep) {
                  event.preventDefault()
                  event.stopPropagation()
                  onOpenStep()
                }
                onClose()
              }}
            >
              This Step
            </Link>{" "}
            has draft Block changes.
          </Trans>
        </Text>
      </HStack>
    )

    if (isMultiStepChange) {
      callout = (
        <Card p={3} mt={3}>
          <HStack>
            <Pathicon icon="caution-triangle" />
            <Text ml={1} fontSize="sm" textAlign="left">
              <Trans i18nKey="block-content-is-shared">
                Block content is{" "}
                <Link
                  color="brand.500"
                  target="_blank"
                  href="https://help.pathwright.com/en/articles/1756938-publish-button-for-blocks-content#what-publishing-means"
                >
                  shared across all Cohort Paths.
                </Link>
              </Trans>
            </Text>
          </HStack>
        </Card>
      )
    }

    renderButton = () => {
      return (
        <Box mt={1} mb={2} maxW="100%">
          <BlocksPublish
            contextKey={`/${item.source_id}`}
            item={item}
            onPublish={onClose}
            label={
              isMultiStepChange
                ? tc("Save Blocks to All Cohort Paths")
                : tc("Save Blocks")
            }
          />
        </Box>
      )
    }
  } else if (
    publishStatus === stepPublishStatuses.newStep ||
    publishStatus === stepPublishStatuses.stepDraft
  ) {
    heading =
      publishStatus === stepPublishStatuses.newStep
        ? tc("Draft")
        : tc("Draft Changes")

    buttonLabel = tc("Review & publish path changes")
    buttonUrl = getSyncUrl()
  } else {
    heading = tc("Published")
    const lastPublished = item?.last_published_dtime
    callout = lastPublished ? (
      <HStack
        justify="center"
        gap={0}
        sx={{
          ".When": {
            fontSize: "var(--chakra-fontSizes-xs)",
            fontStyle: "italic",
            color: "var(--chakra-colors-PRIMARY_GRAY)",
            fontWeight: "500"
          }
        }}
      >
        <Text fontStyle="italic" fontSize="xs" mr="3px">
          {tc("Last published")}
        </Text>
        <When date={new Date(lastPublished)} />
      </HStack>
    ) : null
  }

  return (
    <PublishSectionContent
      heading={heading}
      label={label}
      callout={callout}
      buttonUrl={buttonUrl}
      buttonLabel={buttonLabel}
      renderButton={renderButton}
    />
  )
}

const SyncContentSynced = ({ expanded }: { expanded: boolean }) => {
  const { cohort, resource, item } = usePublish()
  const { tc, Trans } = useTranslate()
  const sourceUrl = getStepUrl(
    resource?.slug,
    cohort!.source?.id!,
    item?.source_id!
  )

  return (
    <>
      <Heading as="h2" my={0}>
        <AccordionButton pt={4} pb={expanded ? 0 : 4}>
          <Flex
            w="100%"
            justify="center"
            direction={expanded ? "column" : "row"}
            align="center"
            gap={1}
          >
            <Pathicon
              icon="cloud-arrows-fill"
              style={{
                fontSize: "1.4em",
                marginBottom: "-4px",
                color: "blue"
              }}
            />
            <Text fontWeight="700">{tc("Syncing with Source Path")}</Text>
          </Flex>
          <AccordionIcon />
        </AccordionButton>
      </Heading>
      <AccordionPanel pb={4} textAlign="center">
        <VStack gap={4}>
          <Text fontSize="sm" mb={4}>
            <Trans
              i18nKey="This Step syncs with its <a>Source Path version</a> and automatically updates when changes in the Source Path are published."
              components={{
                a: <Link textDecoration="underline" href={sourceUrl} />
              }}
            />
          </Text>
          <Text fontStyle="italic" fontSize="sm">
            <Trans i18nKey="you-can-unsync-and-customize">
              <Text as="span" fontWeight="700">
                Tip:
              </Text>{" "}
              you can unsync and customize this Step for{" "}
              <Box as="span" fontWeight="700">
                {cohort!.name}
              </Box>{" "}
              by editing and publishing it here in the Cohort.
            </Trans>
          </Text>
        </VStack>
      </AccordionPanel>
    </>
  )
}

const SyncContentChanged = ({
  expanded,
  mode
}: {
  expanded: boolean
  mode: "changed" | "unique" | "changed-viewonly"
}) => {
  const { cohort, item } = usePublish()
  const { tc, Trans } = useTranslate()

  const isViewOnly = mode === "changed-viewonly"

  const icon = "cloud-asterisk-fill"
  const title =
    mode === "changed" || mode === "changed-viewonly"
      ? tc("Changes not synced with Source Path")
      : tc("Step not synced with Source Path")

  const description =
    mode === "changed-viewonly" ? (
      <Trans
        i18nKey="This Step has changes that are unique to the <cohort>{{cohort}}</cohort> Cohort."
        values={{
          cohort: cohort!.name
        }}
        components={{
          cohort: (
            <Box as="span" fontWeight="700">
              {cohort!.name}
            </Box>
          )
        }}
      />
    ) : mode === "changed" ? (
      <Trans i18nKey="this-step-has-changes">
        This Step has changes that are unique to the{" "}
        <Box as="span" fontWeight="700">
          {cohort!.name}
        </Box>{" "}
        Cohort. To publish these changes to all Cohorts, first:
      </Trans>
    ) : (
      <Trans i18nKey="this-step-is-unique">
        This Step is unique to the{" "}
        <Box as="span" fontWeight="700">
          {cohort!.name}
        </Box>{" "}
        Cohort. To publish it to all Cohorts, first:
      </Trans>
    )

  return (
    <>
      <Heading as="h2" my={0}>
        <AccordionButton pt={4} pb={expanded ? 0 : 4}>
          <Flex
            w="100%"
            justify="center"
            direction={expanded ? "column" : "row"}
            align="center"
            gap={1}
          >
            <Pathicon
              icon={icon}
              style={{
                fontSize: "1.4em",
                marginBottom: "-4px",
                color: "blue"
              }}
            />
            <Text fontWeight="700">{title}</Text>
          </Flex>
          <AccordionIcon />
        </AccordionButton>
      </Heading>
      <AccordionPanel pb={4}>
        <VStack gap={4}>
          <Text fontSize="sm">{description}</Text>
          {!isViewOnly && (
            <SuggestedAction
              label={
                <>
                  <Flex w="100%" justify="flex-start" alignItems="center">
                    <Text as="span" color="white">
                      {tc("Review & sync with")}{" "}
                    </Text>
                    <Pathicon
                      icon="cloud-arrows-fill"
                      style={{
                        display: "inline-block",
                        fontSize: "1.4em",
                        marginLeft: "6px",
                        marginBottom: "-2px"
                      }}
                    />{" "}
                    <Text
                      color="white"
                      overflow="hidden"
                      textOverflow="ellipsis"
                      whiteSpace="nowrap"
                      marginLeft="3px"
                    >
                      {tc("Source Path")}
                    </Text>
                  </Flex>
                </>
              }
              url={`${getSyncUrl()}?dir=backward&sourceIds=${item?.source_id}`}
            />
          )}
        </VStack>
      </AccordionPanel>
    </>
  )
}

const SyncContentViewOnly = ({ expanded }: { expanded: boolean }) => {
  const { tc, Trans } = useTranslate()

  return (
    <>
      <Heading as="h2" my={0}>
        <AccordionButton pt={4} pb={expanded ? 0 : 4}>
          <Flex
            w="100%"
            justify="center"
            direction={expanded ? "column" : "row"}
            align="center"
            gap={1}
          >
            <Pathicon
              icon="cloud-arrows-fill"
              style={{
                fontSize: "1.4em",
                marginBottom: "-4px",
                color: "blue"
              }}
            />
            <Text fontWeight="700">{tc("Synced with Source Path")}</Text>
          </Flex>
          <AccordionIcon />
        </AccordionButton>
      </Heading>
      <AccordionPanel pb={4} textAlign="center">
        <VStack gap={4}>
          <Text fontSize="sm">
            {tc("This Step requires access to the Source Path to edit.")}
          </Text>
          <Text fontStyle="italic" fontSize="sm">
            <Trans
              i18nKey="<s>Tip:</s> to create a custom version for this Cohort, copy & paste it into a new Step and then remove it. Your copy will be fully editable."
              components={{
                s: <Text as="span" fontWeight="700"></Text>
              }}
            />
          </Text>
        </VStack>
      </AccordionPanel>
    </>
  )
}

const SyncSection = () => {
  const { syncStatus, cohort, publishStatus, resource } = usePublish()
  const [expanded, setExpanded] = useState(false)

  if (
    !cohort ||
    syncStatus === "none" ||
    publishStatus !== stepPublishStatuses.published
  )
    return null

  return (
    <Accordion
      // h="max-content"
      allowToggle
      onChange={() => setExpanded(!expanded)}
    >
      <AccordionItem
        border="none"
        h="100%"
        pos="relative"
        sx={{
          "p, i, svg, span": {
            color: "white"
          },
          ".chakra-heading": {
            zIndex: 1
          },
          ".chakra-accordion__panel": {
            zIndex: 1,
            position: "relative"
          },
          ".chakra-accordion__button": {
            backgroundColor: "transparent",
            cursor: "pointer"
          }
        }}
        overflow="hidden"
      >
        <ResourceBackground
          resource={resource}
          overlay={resource.background_overlay_color || "rgba(0, 0, 0, 0.3)"}
          forceBlur
        />
        <DotBackground />
        {syncStatus === "synced" ? (
          <SyncContentSynced expanded={expanded} />
        ) : syncStatus === "changed" ||
          syncStatus === "unique" ||
          syncStatus === "changed-viewonly" ? (
          <SyncContentChanged expanded={expanded} mode={syncStatus} />
        ) : (
          <SyncContentViewOnly expanded={expanded} />
        )}
      </AccordionItem>
    </Accordion>
  )
}

const StepPublishCard = () => {
  const { isOpen, onClose, onOpen } = usePublish()

  const cardRef = useRef<HTMLDivElement>(null)

  const handleOpen = () => {
    const parentContainer = cardRef.current?.closest(
      ".PathStepContainer"
    ) as HTMLElement | null

    // Prevent the popover from rendering underneath subsequence Path Item elements
    if (parentContainer) {
      parentContainer.style.zIndex = "1000"
    }

    onOpen()
  }

  const handleClose = () => {
    const parentContainer = cardRef.current?.closest(
      ".PathStepContainer"
    ) as HTMLElement | null

    if (parentContainer) {
      // reset zindex
      parentContainer.style.zIndex = ""
    }

    onClose()
  }

  return (
    <Popover
      isOpen={isOpen}
      onClose={handleClose}
      onOpen={handleOpen}
      isLazy
      lazyBehavior="keepMounted"
      trigger="hover"
      placement="bottom"
      flip={false}
      preventOverflow
    >
      <PopoverTrigger>
        <Box>
          <StepPublishTrigger />
        </Box>
      </PopoverTrigger>
      <PopoverContent border="none" ref={cardRef}>
        <PopoverArrow />
        <PopoverBody p={0}>
          <VStack
            p={0}
            justify="space-between"
            align="stretch"
            borderBottomRadius="10px" // matches PopoverContent border-radius
            overflow="hidden"
            sx={{
              ".StepPublish__status-icon svg, .StepPublish__status-icon i": {
                height: "27px",
                width: "27px"
              },
              "p.chakra-text": {
                // remove default margin; use Flex gap or custom margin/padding
                marginTop: "unset",
                marginBottom: "unset"
              }
            }}
          >
            <PublishSection />
            <SyncSection />
          </VStack>
        </PopoverBody>
      </PopoverContent>
    </Popover>
  )
}

type StepPublishCardContainerProps = {
  offering: FromCohort | { id: number }
  item: PathItem
  onLaunchSync?: () => void
  onOpenStep?: () => void
}

const StepPublishCardContainer = (props: StepPublishCardContainerProps) => {
  const { offering, item: propsItem } = props

  const { fromCohort: cohort } = useCohortSyncPlanQuery({
    cohortId: offering.id
  })

  if (!cohort || !propsItem) return null

  const item = { ...propsItem }

  // Path items may have source_id or sync_source_id,
  //  depending on whether they're sourced via GQL or the Path store/backend
  // @ts-expect-error: the PathItem doesn't have sync_source_id
  if (item.sync_source_id) {
    // @ts-expect-error: the PathItem doesn't have sync_source_id
    item.source_id = item.sync_source_id
  }

  return (
    <StepPublishProvider {...props} cohort={cohort} item={item}>
      <StepPublishCard />
    </StepPublishProvider>
  )
}

export default StepPublishCardContainer
